/**
 * AgentCorp Virtual Office
 *
 * A delightful 2D isometric visualization of a virtual office where AI agents work.
 * Features animated characters, customizable themes, roaming pets, and real-time activity status.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  renderFurniture,
  getFurnitureMeta,
  DeskSprite,
  ChairSprite,
  SofaSprite,
  PlantSprite,
  RefreshmentSprite,
  ServerSprite,
  StorageSprite,
  DividerSprite,
  WindowSprite,
  RugSprite,
  PetSprite,
} from './OfficeSprites';

const AUTO_DESK_SPACING_X = 150;
const AUTO_DESK_SPACING_Y = 120;

const AREA_LABELS = {
  desk: 'Focused',
  coffee: 'Coffee Run',
  lounge: 'Recharge',
  meeting: 'Huddle',
  stroll: 'Walking',
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const DEFAULT_ROAMING_PETS = [
  { id: 'office-cat', type: 'cat-bed', homeX: 0.84, homeY: 0.86, mood: 'napping' },
  { id: 'office-dog', type: 'dog-bed', homeX: 0.12, homeY: 0.82, mood: 'zoomies' },
  { id: 'office-bird', type: 'bird-perch', homeX: 0.18, homeY: 0.18, mood: 'perched' },
];

const FLOOR_THEMES = {
  wood: {
    background: 'linear-gradient(135deg, #d4c4a8 0%, #c9b896 100%)',
    pattern: 'linear-gradient(45deg, rgba(0,0,0,0.03) 25%, transparent 25%), linear-gradient(-45deg, rgba(0,0,0,0.03) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.03) 75%), linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.03) 75%)',
    size: '40px 40px',
  },
  tile: {
    background: 'linear-gradient(135deg, #dbe4ec 0%, #c7d2dc 100%)',
    pattern: 'linear-gradient(rgba(15,23,42,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.08) 1px, transparent 1px)',
    size: '40px 40px',
  },
  carpet: {
    background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
    pattern: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), radial-gradient(rgba(0,0,0,0.12) 1px, transparent 1px)',
    size: '14px 14px',
  },
  marble: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
    pattern: 'linear-gradient(45deg, rgba(148,163,184,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(148,163,184,0.15) 25%, transparent 25%)',
    size: '40px 40px',
  },
  concrete: {
    background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    pattern: 'radial-gradient(rgba(255,255,255,0.05) 2px, transparent 2px), radial-gradient(rgba(0,0,0,0.1) 1px, transparent 1px)',
    size: '26px 26px',
  },
  grass: {
    background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
    pattern: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
    size: '18px 18px',
  },
  neon: {
    background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
    pattern: 'linear-gradient(rgba(139,92,246,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(236,72,153,0.1) 1px, transparent 1px)',
    size: '28px 28px',
  },
};

const WALL_THEMES = {
  warm: {
    wall: 'linear-gradient(to bottom, #8B7355 0%, #a08060 80%, #6b5344 100%)',
    side: 'linear-gradient(to right, #4a3d32 0%, #5c4a3a 55%, #6d5a4a 100%)',
    trim: '#8B5A2B',
    panel: '#8b7355',
    edge: '#9c8465',
  },
  studio: {
    wall: 'linear-gradient(to bottom, #dbe4ec 0%, #cbd5e1 78%, #94a3b8 100%)',
    side: 'linear-gradient(to right, #94a3b8 0%, #cbd5e1 50%, #e2e8f0 100%)',
    trim: '#64748b',
    panel: '#e2e8f0',
    edge: '#cbd5e1',
  },
  moss: {
    wall: 'linear-gradient(to bottom, #3f6212 0%, #4d7c0f 75%, #365314 100%)',
    side: 'linear-gradient(to right, #365314 0%, #4d7c0f 55%, #65a30d 100%)',
    trim: '#365314',
    panel: '#65a30d',
    edge: '#84cc16',
  },
  neon: {
    wall: 'linear-gradient(to bottom, #1e1b4b 0%, #312e81 75%, #0f172a 100%)',
    side: 'linear-gradient(to right, #0f172a 0%, #312e81 55%, #4c1d95 100%)',
    trim: '#7c3aed',
    panel: '#312e81',
    edge: '#8b5cf6',
  },
  sunset: {
    wall: 'linear-gradient(to bottom, #7c2d12 0%, #c2410c 80%, #7c2d12 100%)',
    side: 'linear-gradient(to right, #9a3412 0%, #c2410c 55%, #fb923c 100%)',
    trim: '#ea580c',
    panel: '#fdba74',
    edge: '#fb923c',
  },
};

const getAgentPalette = (agent, index) => {
  const fallbackColors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
  const primary = agent.color || fallbackColors[index % fallbackColors.length];
  return {
    primary,
    secondary: `${primary}cc`,
    accent: '#f8fafc',
  };
};

/**
 * Character Sprite Component
 * Renders an animated agent character with status indicators
 */
const CharacterSprite = ({
  x,
  y,
  agent,
  palette,
  isActive,
  isSitting,
  status,
  customerName,
  areaLabel,
  onClick,
  isDollhouseView = false,
}) => {
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFrame((current) => (current + 1) % 4);
    }, isActive ? 180 : 340);
    return () => clearInterval(interval);
  }, [isActive]);

  const idleLift = isActive ? Math.sin(frame * (Math.PI / 2)) * 3 : Math.sin(frame) * 1.5;
  const armSwing = status === 'stroll' || status === 'coffee' ? Math.sin(frame) * 14 : isActive ? Math.sin(frame) * 8 : 0;

  // 3D transform for dollhouse view
  const dollhouseTransform = isDollhouseView ? {
    transform: 'rotateZ(45deg) rotateX(-55deg)',
    transformOrigin: 'center bottom',
    filter: 'drop-shadow(4px 8px 6px rgba(0,0,0,0.4))',
  } : {};

  return (
    <div
      className="absolute cursor-pointer transition-[left,top] duration-[1800ms] ease-in-out"
      style={{ left: x, top: y - idleLift, zIndex: Math.floor(y) + 100, transformStyle: 'preserve-3d' }}
      onClick={onClick}
    >
      <div style={dollhouseTransform}>
        {(customerName || areaLabel) && (
          <div className="absolute -top-10 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap">
            <div className="rounded-xl bg-white px-2 py-1 text-[10px] font-semibold text-slate-800 shadow-xl">
              {customerName || areaLabel}
            </div>
            <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-white" />
          </div>
        )}

        <div className={`relative ${isSitting ? 'scale-95' : ''}`} style={{ width: 30, height: 44 }}>
          <div className="absolute bottom-0 left-2 h-2 w-6 rounded-full bg-black/20" />
          <div className="absolute left-8 top-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-black/60 text-sm shadow-lg">
            {agent.emoji || '🧑'}
          </div>
          <div className="absolute left-8 top-0 h-2 w-2 rounded-full bg-[#fbbf24] animate-pulse" />

          <div className="absolute left-[7px] top-1 h-11 w-5 rounded-full" style={{ backgroundColor: '#111827' }} />
          <div className="absolute left-[9px] top-[3px] h-4 w-4 rounded-full bg-[#f5d0b5]">
            <div className="absolute left-[2px] top-[4px] h-[2px] w-[2px] rounded-full bg-slate-900" />
            <div className="absolute right-[2px] top-[4px] h-[2px] w-[2px] rounded-full bg-slate-900" />
            {isActive && <div className="absolute bottom-[2px] left-[4px] h-[2px] w-[6px] rounded-full border-b border-slate-900" />}
          </div>

          <div className="absolute left-[5px] top-[17px] h-4 w-6 rounded-xl shadow-md" style={{ background: `linear-gradient(180deg, ${palette.primary} 0%, ${palette.secondary} 100%)` }} />
          <div className="absolute left-[2px] top-[18px] h-3 w-2 rounded-full" style={{ backgroundColor: palette.primary, transform: `rotate(${armSwing}deg)`, transformOrigin: 'top center' }} />
          <div className="absolute right-[2px] top-[18px] h-3 w-2 rounded-full" style={{ backgroundColor: palette.primary, transform: `rotate(${-armSwing}deg)`, transformOrigin: 'top center' }} />
          {!isSitting && (
            <>
              <div className="absolute left-[9px] top-[30px] h-4 w-2 rounded-b-md bg-[#1e3a5f]" />
              <div className="absolute right-[9px] top-[30px] h-4 w-2 rounded-b-md bg-[#1e3a5f]" />
            </>
          )}
        </div>

        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
          <span
            className="rounded-md px-1.5 py-0.5 text-[9px] font-bold text-white shadow-lg"
            style={{ backgroundColor: isActive ? '#22c55e' : 'rgba(15,23,42,0.85)' }}
          >
            {agent.name}
          </span>
        </div>

        <div className="absolute -top-7 left-1/2 -translate-x-1/2 rounded-full border border-white/15 bg-black/70 px-1.5 py-0.5 text-[8px] font-semibold text-white shadow-lg">
          {status === 'desk' ? 'On Queue' : AREA_LABELS[status] || 'Idle'}
        </div>
      </div>
    </div>
  );
};

CharacterSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    emoji: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  palette: PropTypes.shape({
    primary: PropTypes.string.isRequired,
    secondary: PropTypes.string.isRequired,
    accent: PropTypes.string,
  }).isRequired,
  isActive: PropTypes.bool,
  isSitting: PropTypes.bool,
  status: PropTypes.string,
  customerName: PropTypes.string,
  areaLabel: PropTypes.string,
  onClick: PropTypes.func,
  isDollhouseView: PropTypes.bool,
};

/**
 * VirtualOffice Component
 *
 * Main visualization component for the virtual AI office.
 *
 * @param {Array} agents - Array of agent objects with id, name, emoji, color
 * @param {Object} activeEvents - Map of agentId to active event (customer being served)
 * @param {string} officeName - Name displayed at the top of the office
 * @param {Object} layout - Custom layout configuration with items and appearance
 * @param {Object} theme - Theme configuration including environment
 * @param {Object} settings - Display settings (showAccessories, etc.)
 * @param {boolean} isDollhouseView - Enable 3D dollhouse perspective
 * @param {function} onAgentClick - Callback when an agent is clicked
 */
const VirtualOffice = ({
  agents = [],
  activeEvents = {},
  officeName = 'AI Office',
  bannerTitle = null,
  layout = null,
  theme = {},
  settings = {},
  isDollhouseView = false,
  onAgentClick,
}) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 980, height: 620 });
  const [agentMoods, setAgentMoods] = useState({});
  const [petMoods, setPetMoods] = useState({});

  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      const { width } = containerRef.current.getBoundingClientRect();
      setDimensions({
        width: Math.max(760, Math.round(width)),
        height: Math.max(440, Math.round(width * 0.58)),
      });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const { width, height } = dimensions;

  const deskAnchors = useMemo(() => {
    const deskItems = (layout?.items || []).filter((item) => item.type.includes('desk'));
    if (deskItems.length > 0) {
      return deskItems.map((desk, index) => ({
        deskX: desk.x,
        deskY: desk.y,
        chairX: desk.x + 22,
        chairY: desk.y + 18,
        charX: desk.x + 24,
        charY: desk.y + 20,
        index,
      }));
    }

    const cols = Math.max(2, Math.min(4, Math.ceil(Math.sqrt(Math.max(agents.length, 1)))));
    return agents.map((_, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const baseX = 110 + col * AUTO_DESK_SPACING_X;
      const baseY = 140 + row * AUTO_DESK_SPACING_Y;
      return {
        deskX: baseX,
        deskY: baseY,
        chairX: baseX + 22,
        chairY: baseY + 18,
        charX: baseX + 24,
        charY: baseY + 18,
        index,
      };
    });
  }, [agents, layout]);

  const zones = useMemo(() => ({
    coffee: { x: 58, y: height - 110 },
    lounge: { x: width - 165, y: height - 120 },
    meeting: { x: width / 2 - 40, y: height - 135 },
    stroll: { x: width / 2 + 70, y: 110 },
  }), [height, width]);

  useEffect(() => {
    const nextState = {};
    agents.forEach((agent, index) => {
      const desk = deskAnchors[index % Math.max(deskAnchors.length, 1)] || deskAnchors[0];
      if (!desk) return;
      nextState[agent.id] = {
        x: desk.charX,
        y: desk.charY,
        isSitting: true,
        status: activeEvents[agent.id] ? 'desk' : 'desk',
      };
    });
    setAgentMoods(nextState);
  }, [agents, activeEvents, deskAnchors]);

  useEffect(() => {
    if (agents.length === 0) return undefined;

    const choices = ['desk', 'coffee', 'lounge', 'meeting', 'stroll'];
    const interval = setInterval(() => {
      setAgentMoods((current) => {
        const next = { ...current };
        agents.forEach((agent, index) => {
          const desk = deskAnchors[index % Math.max(deskAnchors.length, 1)] || deskAnchors[0];
          if (!desk) return;

          if (activeEvents[agent.id]) {
            next[agent.id] = {
              x: desk.charX,
              y: desk.charY,
              isSitting: true,
              status: 'desk',
            };
            return;
          }

          const roll = choices[Math.floor(Math.random() * choices.length)];
          if (roll === 'desk') {
            next[agent.id] = {
              x: desk.charX,
              y: desk.charY,
              isSitting: true,
              status: 'desk',
            };
            return;
          }

          const zone = zones[roll];
          next[agent.id] = {
            x: clamp(zone.x + (Math.random() * 60 - 30), 30, width - 90),
            y: clamp(zone.y + (Math.random() * 46 - 23), 90, height - 80),
            isSitting: roll === 'lounge' || roll === 'meeting',
            status: roll,
          };
        });
        return next;
      });
    }, 2800);

    return () => clearInterval(interval);
  }, [activeEvents, agents, deskAnchors, height, width, zones]);

  const ambience = useMemo(() => {
    const environment = theme?.environment || 'corporate';
    const map = {
      gaming: 'linear-gradient(135deg, #111827 0%, #312e81 45%, #1d4ed8 100%)',
      cafe: 'linear-gradient(135deg, #5b341d 0%, #7c4a24 45%, #d97706 100%)',
      beach: 'linear-gradient(135deg, #0f766e 0%, #06b6d4 50%, #fde68a 100%)',
      space: 'linear-gradient(135deg, #020617 0%, #0f172a 55%, #155e75 100%)',
      japanese: 'linear-gradient(135deg, #2d1b10 0%, #5b4033 55%, #b45309 100%)',
      classroom: 'linear-gradient(135deg, #365314 0%, #4d7c0f 50%, #bef264 100%)',
      homeCozy: 'linear-gradient(135deg, #5b3a29 0%, #92400e 55%, #f59e0b 100%)',
    };
    return map[environment] || 'linear-gradient(135deg, #d7c3a3 0%, #ceb590 65%, #b08b62 100%)';
  }, [theme]);

  const hour = new Date().getHours();
  const isNight = hour < 7 || hour >= 19;

  const furnitureItems = layout?.items || [];
  const layoutAppearance = layout?.appearance || {};
  const floorAppearance = FLOOR_THEMES[layoutAppearance.floorType || layout?.floorType] || FLOOR_THEMES.wood;
  const wallAppearance = WALL_THEMES[layoutAppearance.wallTheme] || WALL_THEMES.warm;
  const hasLayoutDesks = furnitureItems.some((item) => item.type.includes('desk'));
  const placedPets = useMemo(() =>
    furnitureItems.filter((item) => getFurnitureMeta(item.type).kind === 'pet'),
    [furnitureItems]
  );
  const roamingPets = useMemo(() => {
    if (placedPets.length > 0) {
      return placedPets.map((item, index) => ({
        id: item.id || `layout-pet-${index}`,
        type: item.type,
        homeX: item.x,
        homeY: item.y,
      }));
    }
    return DEFAULT_ROAMING_PETS.map((pet) => ({
      ...pet,
      homeX: Math.round(width * pet.homeX),
      homeY: Math.round(height * pet.homeY),
    }));
  }, [placedPets, width, height]);

  useEffect(() => {
    const nextPets = {};
    roamingPets.forEach((pet, index) => {
      nextPets[pet.id] = {
        x: pet.homeX,
        y: pet.homeY,
        mood: pet.mood || (index % 2 === 0 ? 'wandering' : 'napping'),
      };
    });
    setPetMoods(nextPets);
  }, [roamingPets]);

  useEffect(() => {
    if (roamingPets.length === 0) return undefined;

    const moodOptions = ['wandering', 'zoomies', 'napping', 'perched', 'bouncing'];
    const interval = setInterval(() => {
      setPetMoods((current) => {
        const next = { ...current };
        roamingPets.forEach((pet) => {
          const mood = moodOptions[Math.floor(Math.random() * moodOptions.length)];
          const spanX = pet.type.includes('bird') ? 80 : 55;
          const spanY = pet.type.includes('bird') ? 45 : 28;
          next[pet.id] = {
            x: clamp(pet.homeX + (Math.random() * spanX - spanX / 2), 24, width - 48),
            y: clamp(pet.homeY + (Math.random() * spanY - spanY / 2), 40, height - 28),
            mood,
          };
        });
        return next;
      });
    }, 3200);

    return () => clearInterval(interval);
  }, [height, roamingPets, width]);

  // Dollhouse view transform styles
  const dollhouseStyle = isDollhouseView
    ? {
        transform: 'perspective(1200px) rotateX(55deg) rotateZ(-45deg) scale(0.7)',
        transformOrigin: 'center center',
        transition: 'transform 0.6s ease-in-out',
      }
    : {
        transform: 'perspective(1200px) rotateX(0deg) rotateZ(0deg) scale(1)',
        transformOrigin: 'center center',
        transition: 'transform 0.6s ease-in-out',
      };

  return (
    <div ref={containerRef} className="h-full w-full flex items-center justify-center" style={{ minHeight: isDollhouseView ? '700px' : 'auto' }}>
      <div
        className="relative overflow-hidden rounded-[28px] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
        style={{ width, height, background: floorAppearance.background, ...dollhouseStyle }}
      >
        <div className="absolute inset-0 opacity-30" style={{ background: ambience }} />
        <div
          className="absolute inset-0 opacity-35"
          style={{
            backgroundImage: floorAppearance.pattern,
            backgroundSize: floorAppearance.size,
            backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
          }}
        />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.35), transparent 30%), radial-gradient(circle at 80% 12%, rgba(34,211,238,0.25), transparent 22%)' }} />
        {isNight && <div className="absolute inset-0 bg-[#020617]/25" />}

        {/* 3D Walls for Dollhouse View */}
        {isDollhouseView && (
          <>
            {/* Back wall */}
            <div
              className="absolute inset-x-0 top-0 bg-gradient-to-b from-[#5c4a3a] via-[#6d5a4a] to-[#7c5b3d]"
              style={{
                height: '120px',
                transformOrigin: 'bottom center',
                transform: 'rotateX(-90deg) translateZ(0px)',
                boxShadow: 'inset 0 -20px 40px rgba(0,0,0,0.3)',
                background: wallAppearance.wall,
              }}
            >
              {/* Wall decorations */}
              <div className="absolute inset-0 flex items-center justify-around px-8">
                <div className="w-16 h-12 rounded border-2 shadow-inner" style={{ backgroundColor: wallAppearance.panel, borderColor: wallAppearance.edge }} />
                <div className="w-20 h-14 rounded border-2 shadow-inner" style={{ backgroundColor: wallAppearance.panel, borderColor: wallAppearance.edge }} />
                <div className="w-16 h-12 rounded border-2 shadow-inner" style={{ backgroundColor: wallAppearance.panel, borderColor: wallAppearance.edge }} />
              </div>
            </div>
            {/* Left wall */}
            <div
              className="absolute left-0 top-0 bottom-0"
              style={{
                width: '100px',
                transformOrigin: 'right center',
                transform: 'rotateY(90deg) translateZ(0px)',
                boxShadow: 'inset -20px 0 40px rgba(0,0,0,0.3)',
                background: wallAppearance.side,
              }}
            >
              {/* Wall decorations */}
              <div className="absolute inset-0 flex flex-col items-center justify-around py-8">
                <div className="w-12 h-10 rounded border-2 shadow-inner" style={{ backgroundColor: wallAppearance.panel, borderColor: wallAppearance.edge }} />
                <div className="w-14 h-10 rounded border-2 shadow-inner" style={{ backgroundColor: wallAppearance.panel, borderColor: wallAppearance.edge }} />
              </div>
            </div>
          </>
        )}

        <div className="absolute inset-x-0 top-0 h-28" style={{ background: `linear-gradient(to bottom, ${wallAppearance.trim}bb 0%, transparent 100%)` }} />
        <div className="absolute left-1/2 top-5 z-20 -translate-x-1/2 rounded-2xl border border-white/15 bg-slate-950/70 px-5 py-3 text-white shadow-2xl backdrop-blur">
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.35em] text-white/50">Live Office</p>
            <h3 className="text-lg font-bold">{bannerTitle || officeName}</h3>
            <p className="text-xs text-white/60">{agents.length} agents roaming the floor</p>
          </div>
        </div>

        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:28px_28px] opacity-40" />

        <div className="absolute left-8 top-14 z-10">
          <WindowSprite x={0} y={0} isDollhouseView={isDollhouseView} />
          <WindowSprite x={86} y={0} isDollhouseView={isDollhouseView} />
        </div>
        <div className="absolute right-8 top-14 z-10">
          <WindowSprite x={0} y={0} isDollhouseView={isDollhouseView} />
        </div>

        <RugSprite x={width / 2 - 90} y={height - 168} width={180} height={96} isDollhouseView={isDollhouseView} />
        <SofaSprite x={width - 180} y={height - 122} isDollhouseView={isDollhouseView} />
        <PlantSprite x={width - 75} y={height - 92} isDollhouseView={isDollhouseView} />
        <PlantSprite x={35} y={95} large={true} isDollhouseView={isDollhouseView} />
        <RefreshmentSprite x={40} y={height - 115} type="coffee-machine" isDollhouseView={isDollhouseView} />
        <ServerSprite x={width - 70} y={150} active={true} isDollhouseView={isDollhouseView} />
        <StorageSprite x={width - 92} y={88} isDollhouseView={isDollhouseView} />
        <DividerSprite x={width / 2 - 60} y={height - 206} width={132} isDollhouseView={isDollhouseView} />
        {furnitureItems.map((item, index) => renderFurniture(item, index, false, isDollhouseView))}

        {!hasLayoutDesks && deskAnchors.map((desk, index) => (
          <React.Fragment key={`auto-desk-${index}`}>
            <DeskSprite x={desk.deskX} y={desk.deskY} variant={theme?.environment === 'gaming' ? 'gaming' : 'modern'} active={Boolean(activeEvents[agents[index]?.id])} isDollhouseView={isDollhouseView} />
            <ChairSprite x={desk.chairX} y={desk.chairY} color={theme?.environment === 'gaming' ? '#7c3aed' : '#475569'} isDollhouseView={isDollhouseView} />
          </React.Fragment>
        ))}

        {agents.map((agent, index) => {
          const mood = agentMoods[agent.id] || {};
          const isActive = Boolean(activeEvents[agent.id]);
          const areaLabel = !isActive && mood.status && mood.status !== 'desk' ? AREA_LABELS[mood.status] : null;
          return (
            <CharacterSprite
              key={agent.id}
              x={mood.x || 0}
              y={mood.y || 0}
              agent={agent}
              palette={getAgentPalette(agent, index)}
              isActive={isActive}
              isSitting={Boolean(mood.isSitting)}
              status={mood.status || 'desk'}
              customerName={isActive ? activeEvents[agent.id]?.customer?.name : null}
              areaLabel={areaLabel}
              onClick={() => onAgentClick?.(agent)}
              isDollhouseView={isDollhouseView}
            />
          );
        })}

        {roamingPets.map((pet) => {
          const mood = petMoods[pet.id] || { x: pet.homeX, y: pet.homeY, mood: 'wandering' };
          return (
            <PetSprite
              key={pet.id}
              x={mood.x}
              y={mood.y}
              type={pet.type}
              mood={mood.mood}
              roaming={true}
              isDollhouseView={isDollhouseView}
            />
          );
        })}

        <div className="absolute bottom-4 left-4 z-20 rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white backdrop-blur">
          <p className="text-[10px] uppercase tracking-[0.28em] text-white/40">Office Pulse</p>
          <div className="mt-2 flex gap-4 text-xs">
            <span>{Object.keys(activeEvents).length} serving</span>
            <span>{furnitureItems.length} props placed</span>
            <span>{settings?.showAccessories === false ? 'clean desks' : 'decor on'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

VirtualOffice.propTypes = {
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      emoji: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  activeEvents: PropTypes.objectOf(
    PropTypes.shape({
      customer: PropTypes.shape({
        name: PropTypes.string,
      }),
    })
  ),
  officeName: PropTypes.string,
  bannerTitle: PropTypes.string,
  layout: PropTypes.shape({
    items: PropTypes.array,
    appearance: PropTypes.shape({
      floorType: PropTypes.string,
      wallTheme: PropTypes.string,
    }),
    floorType: PropTypes.string,
  }),
  theme: PropTypes.shape({
    environment: PropTypes.string,
  }),
  settings: PropTypes.shape({
    showAccessories: PropTypes.bool,
  }),
  isDollhouseView: PropTypes.bool,
  onAgentClick: PropTypes.func,
};

export default VirtualOffice;
